/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ImageWidget", ["ColoredWidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Image widget.
   * @class ImageWidget
   * @memberOf classes
   * @extends classes.ColoredWidgetBase
   * @publicdoc Widgets
   */
  cls.ImageWidget = context.oo.Class(cls.ColoredWidgetBase, function ($super) {
    return /** @lends classes.ImageWidget.prototype */ {
      __name: "ImageWidget",
      /**
       * @type {?string}
       */
      _src: null,
      _defaultColor: null,
      /** @type {boolean} */
      _autoScale: false,
      /** @type {String|null} **/
      _scaleIconValue: null,

      /** @type {boolean} */
      _gotFirstInitialImage: false,
      /** @type {boolean} */
      _firstInitialSizing: true,
      /** @type {boolean} */
      _initialAutoscaling: false,
      /** @type {HTMLElement} */
      _img: null,
      /** @type {?Function} */
      _boundOnLoad: null,
      /** @type {?Function} */
      _boundOnError: null,
      /** @type {HTMLElement} */
      _border: null,
      /** @type {boolean} */
      _standalone: false,
      /** @type {boolean} */
      _hasContent: false,
      /** @type {Object} */
      _alignment: null,
      /** @type {number} */
      _rowIndex: -1,
      /** @type {String|null} **/
      _imageBorderRadius: null,
      /**
       * @type {Function}
       */
      _resizeObserver: null,
      /**
       * @type {Function}
       */
      _animationFrameId: null,

      /**
       * Custom error handler called inside _onError
       * @type {function} */
      _onErrorHandler: null,

      /**
       * @type {Boolean}
       */
      _isClickable: false,

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        this._layoutInformation = new cls.LayoutInformation(this);
        this._layoutInformation.shouldFillStack = true;
        this._layoutEngine = new cls.ImageLayoutEngine(this);
        this._layoutEngine._shouldFillHeight = true;
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._border = null;
        this._cleanupContent();
        this._boundOnLoad = null;
        this._boundOnError = null;
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        this._onRequestFocus(domEvent); // request focus
        this.emit(context.constants.widgetEvents.click, domEvent);
        if (this._isClickable) {
          return false; // don't propagate event on parent widget
        }
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      manageMouseDblClick: function (domEvent) {
        // request Focus already done by singleClick
        this.emit(context.constants.widgetEvents.doubleClick, domEvent);
        return true;
      },

      /**
       * Define image as a regular standalone widget
       * @param {boolean} standalone - true if standalone, false otherwise
       */
      setStandaloneImage: function (standalone) {
        this._standalone = Boolean(standalone);
        this.toggleClass("gbc_withBorder", this._standalone);
        this.toggleClass("gbc_selfImage", this._standalone);
      },

      /**
       * If image has action, change cursor
       * @param {boolean} clickable - true if clickable, false otherwise
       * @publicdoc
       */
      setClickableImage: function (clickable) {
        this._isClickable = clickable;
        this.toggleClasses(clickable, "clickable");
        this.setAriaRole(clickable ? "button" : null);
      },

      /**
       * ShortCut for setSrc
       * This is used in the context of an Image FormField
       * @param {string} val the URL of the image to display or a font-image URL: font:[fontname]:[character]:[color]
       * @see setSrc
       * @publicdoc
       */
      setValue: function (val) {
        this.setSrc(val);
      },

      /**
       * Shortcut for getSrc
       * This is used in the context of an Image FormField
       * @returns {string} the URL of the displayed image or a font-image URL: font:[fontname]:[character]:[color]
       * @see getSrc
       * @publicdoc
       */
      getValue: function () {
        return this.getSrc();
      },

      /**
       * ShortCut for setSrc
       * This is used in the context of a Static Image
       * @param {string} image the URL of the image to display or a font-image URL: font:[fontname]:[character]:[color]
       * @see setSrc
       * @publicdoc
       */
      setImage: function (image) {
        this.setSrc(image);
      },

      /**
       * Shortcut for getSrc
       * This is used in the context of a Static FormField
       * @returns {string} the URL of the displayed image or a font-image URL: font:[fontname]:[character]:[color]
       * @see getSrc
       * @publicdoc
       */
      getImage: function () {
        return this.getSrc();
      },

      /**
       * Check if image is a font image
       * @return {boolean} true if is a font image
       * @publicdoc
       */
      isFontImage: function () {
        if (this._src) {
          return this._src.startsWith("font:");
        }
        return false;
      },

      /**
       * @inheritDoc
       */
      isInterruptable: function () {
        return this.getParentWidget()?.isInterruptable();
      },

      /**
       * Set the source of the image file
       * @param {string} src the URL of the image to display or a font-image URL: font:[fontname]:[character]:[color]
       * @param {boolean?} directApply true to apply src directly (internal use)
       * @publicdoc
       */
      setSrc: function (src, directApply) {
        this.getLayoutInformation().invalidateMeasure();
        if (src?.startsWith("genero-content")) {
          // Fetch the associated the media blob url
          src = context.SessionService.getCurrent().getCurrentApplication().filetransfer.getFileBlob(src);
        }
        if (src !== this._src) {
          const old = this._src;
          const initial = this.getLayoutInformation().getSizePolicyConfig().isInitial();
          this._src = src;
          if (initial && src !== null) {
            if (this._gotFirstInitialImage && old !== null) {
              this._firstInitialSizing = false;
            }
            this._gotFirstInitialImage = true;
          }
          this._updateImage(directApply);
        }
        const title = this.getTitle();
        // alt only applies to <img>; font icons (<i>/<svg>) are decorative and aria-hidden.
        // Always set it: the comment when informative, empty ("") when decorative so AT skips it.
        if (this._img?.tagName === "IMG") {
          let altText = "";
          if (title) {
            altText = title;
          }
          if (this._img.getAttribute("alt") !== altText) {
            this.domAttributesMutator(
              function () {
                if (!this._destroyed && this._img?.tagName === "IMG") {
                  this._img.setAttribute("alt", altText);
                }
              }.bind(this)
            );
          }
        }
      },

      /**
       * Get the source of the image file
       * @returns {string} the URL of the displayed image or a font-image URL: font:[fontname]:[character]:[color]
       * @publicdoc
       */
      getSrc: function () {
        return this._src;
      },

      /**
       * @inheritDoc
       */
      setTitle: function (title) {
        $super.setTitle.call(this, title);
        if (this._img?.tagName === "IMG") {
          // Empty ("") when there is no comment → decorative image AT skips, never alt="null".
          let altText = "";
          if (title) {
            altText = title;
          }
          this._img.setAttribute("alt", altText);
        }
      },

      /**
       * Define the image as stretchable
       * @param {boolean} stretch - true if stretchable
       * @publicdoc
       */
      setStretch: function (stretch) {
        this.toggleClass("stretch", stretch);
      },

      /**
       * Forces the image to be stretched to fit in the area reserved for the image.
       * @param {boolean} setted true : autoScale , false: default
       * @publicdoc
       */
      setAutoScale: function (setted) {
        if (setted !== this._autoScale) {
          this._autoScale = setted;
          this._updateImage();
        }
      },

      /**
       * Set autoscale value as nnnpx
       * @param {string} value - css string value with valid units
       */
      setScaleIconValue: function (value) {
        this._scaleIconValue = value;
        this.addClasses("gbc_scaleIconValue");
        this._updateImage(true);
      },

      /**
       * Se the default color
       * @param {string} color - any CSS compliant color
       */
      setDefaultColor: function (color) {
        this._defaultColor = color;
        this._updateImage(true);
      },

      /**
       * @inheritDoc
       */
      setFocus: function (fromMouse = false, preventScroll = false) {
        this.domFocus(preventScroll);
      },

      /**
       * @inheritDoc
       */
      getValueForClipboard: function (ignoreSelection = false) {
        return "";
      },

      /**
       * Align the image
       * @param {number|string} y - y positionsetSrc(imgSrc, true)
       * @param {number|string} x - x
       * @publicdoc
       */
      setAlignment: function (y, x) {
        const rtl = this.getStart() === "right";

        let hAlign = (x === "right" && !rtl) || (x !== "right" && rtl) ? "right" : "left";
        hAlign = x === "horizontalCenter" || x === "center" ? "center" : hAlign;
        let vAlign = y === "bottom" ? "bottom" : "top";
        vAlign = y === "verticalCenter" || y === "center" ? "center" : vAlign;
        this._alignment = {
          x: x,
          y: y,
          val: `${hAlign} ${vAlign}`
        };

        const posY = y === "bottom" ? "flex-end" : "flex-start";
        const posX = x === "right" ? "flex-end" : "flex-start";
        this.setStyle({
          "align-items": y === "verticalCenter" ? "center" : posY,
          "justify-content": x === "horizontalCenter" ? "center" : posX
        });
      },

      /**
       * Set a border radius on the image
       * @param {string} borderRadius - accept percent or any CSS related border-radius values
       */
      setImageBorderRadius: function (borderRadius) {
        if (this._imageBorderRadius !== borderRadius) {
          this._imageBorderRadius = borderRadius;
          this.getElement().style.setProperty("--imageBorderRadius", this._imageBorderRadius);
        }
      },

      /**
       * Return the image border radius
       * @return {string}
       */
      getImageBorderRadius: function () {
        return this._imageBorderRadius;
      },

      /**
       * Update image according to several pre-set parameters
       * @param {boolean} directApply true to apply src directly (internal use)
       * @private
       */
      _updateImage: function (directApply) {
        if (!this._element) {
          return;
        }

        if (!this._src) {
          this._cleanupContent();
          return;
        }

        const type = this._detectSourceType(this._src);
        // Reuse the existing <img> when the new source is also a regular image URL.
        // Avoids the empty-frame flicker caused by replacing the element during scroll in scrollgrids/tables.
        const canReuseImg = type === "img" && this._img?.tagName === "IMG";

        if (!canReuseImg) {
          this._cleanupContent();
        }

        switch (type) {
          case "svg":
            this._applyFontSvg();
            break;
          case "i":
            this._applyFontIcon();
            break;
          case "img":
          default:
            this._applyImage(directApply);
            break;
        }

        if (!canReuseImg) {
          if (this._standalone) {
            this._ensureBorder();
          }
          if (this.__charMeasurer) {
            this._element.appendChild(this.__charMeasurer);
          }
          this._hasContent = true;
        }

        this.toggleClass("gbc_autoScale", this._autoScale);
        if (this._scaleIconValue && this._img) {
          this._img.style.setProperty("--scaleIconValue", this._scaleIconValue);
          this.getLayoutInformation().invalidateMeasure();
        }
        this.emit(context.constants.widgetEvents.ready);
      },

      /**
       * Return image type depending on src value pattern. We may generate image using <svg>, <img> or <i>
       * @param src
       * @return {string}
       * @private
       */
      _detectSourceType: function (src) {
        if (src.startsWith("font:")) {
          return "svg";
        } else if (/[/\\.?:]/.test(src)) {
          // idea here is to exclude any css class name possibility (classname => i).
          // since css class names are not supposed to contain special char, we check main special chars which can be contained in any sort of generic path url
          // so we check if src contains at least one of the special char /\.?:
          return "img";
        }
        return "i";
      },

      /**
       * Clear listeners and destroy image references
       * @private
       */
      _cleanupContent: function () {
        if (this._hasContent) {
          cls.DOMElementHelper.empty(this._element);
          this._hasContent = false;
        }
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }

        if (this._img) {
          cls.DOMElementHelper.off(this._img, "error.ImageWidget");
          cls.DOMElementHelper.off(this._img, "load.ImageWidget");
          this._img = null;
        }
      },

      /**
       * Add border on image
       * @private
       */
      _ensureBorder: function () {
        if (!this._border) {
          this._border = document.createElement("div");
          this._border.classList.add("gbc_ImageWidget_border");
        }
        this._element.appendChild(this._border);
      },

      /**
       * Setup a Resize Observer to update scaling ratio of the image each time the widget width/height gets updated
       * @private
       */
      _observeImageScalingRatio: function () {
        // Init Resize Observer
        if (!this._resizeObserver) {
          this._resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
              const { width, height } = entry.contentRect;
              if (this._animationFrameId) {
                this._clearAnimationFrame(this._animationFrameId);
              }
              this._animationFrameId = this._registerAnimationFrame(this._updateImageScalingRatio.bind(this, width, height));
            }
          });

          this._resizeObserver.observe(this.getElement());
        }
      },

      /**
       * Update scaling ratio of the image depending on axis dominance (portrait/landscape) of the widget
       * @param width
       * @param height
       * @private
       */
      _updateImageScalingRatio: function (width, height) {
        if (!this._img?.naturalWidth || !this._img?.naturalHeight) return;

        const imgRatio = this._img.naturalWidth / this._img.naturalHeight;
        const parentRatio = width / height;

        this._img.classList.toggle("fit-width", imgRatio > parentRatio);
        this._img.classList.toggle("fit-height", imgRatio <= parentRatio);
      },

      /**
       * Handle <img> source update: reuse existing element when possible, otherwise create a new one.
       * Reusing the same <img> avoids the flicker caused by DOM replacement during scrollgrid/table scroll.
       * @param directApply
       * @private
       */
      _applyImage: function (directApply) {
        if (this._img?.tagName === "IMG") {
          // Reuse existing <img>: browser keeps old pixels until the new image is ready.
          if (directApply) {
            this._img.src = this._src;
          } else {
            this._setElementAttribute("src", this._src, "_img");
          }
        } else {
          this._appendImage(directApply);
        }
        this._applyImageScaling();
      },

      /**
       * Apply scaling rules on <img> element
       * @private
       */
      _applyImageScaling: function () {
        if (!this._img) {
          return;
        }
        // If image is in a table or should autoscale, then add object-fit and object-position rules. Else do nothing and let default widget CSS rules
        if (this._shouldScale()) {
          const imgStyle = this._img.style;
          imgStyle.width = "100%";
          imgStyle.height = "100%";
          imgStyle.objectFit = "contain";
          imgStyle.objectPosition = (this._alignment && this._alignment.val) || this.getStart();
        }
      },

      /**
       * Indicates if image should scale in width/height to its parent div
       * @return {boolean|*|boolean}
       * @private
       */
      _shouldScale: function () {
        const isInitial = this.getLayoutInformation().getSizePolicyConfig().isInitial();
        // If image is in a table or should autoscale, then add object-fit and object-position rules. Else do nothing and let default widget CSS rules
        return this._inTable || (this._autoScale && !this._inToolBar && (!isInitial || !this._firstInitialSizing));
      },

      /**
       * Create font-based icon (zmdi)
       * @private
       */
      _applyFontIcon: function () {
        this._img = document.createElement("i");
        // Decorative font glyph (PUA char injected via CSS): hide from AT instead of an unnamed role="img"
        this._img.setAttribute("aria-hidden", "true");
        this._img.className = "zmdi " + this._src;
        this._element.appendChild(this._img);
      },

      /**
       * Create image as a svg
       * @private
       */
      _applyFontSvg: function () {
        const pattern = /font:([^:]+).ttf:([^:]+):?([^:]*)/,
          match = this._src.match(pattern);
        let fontName, sCharCode, color, iCharCode, finalChar;
        if (match) {
          fontName = match[1];
          sCharCode = match[2];
          iCharCode = Number.parseInt("0x" + sCharCode, 16);
          finalChar = String.fromCodePoint(iCharCode);
          color = match[3] || this._defaultColor;
        }
        if (fontName && sCharCode) {
          this._img = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          // Decorative font glyph rendered as SVG (PUA char in <text>): hide from AT
          this._img.setAttribute("aria-hidden", "true");
          this._img.setAttribute("viewBox", "0 0 640 512");
          // to left align svg, we need to set xMin, otherwise with a 100% width viewBox it will be centered
          this._img.setAttribute("preserveAspectRatio", "xMinYMid meet");
          const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
          text.setAttribute("text-anchor", "middle");
          text.setAttribute("dominant-baseline", "central");
          text.setAttribute("x", "320");
          text.setAttribute("y", "256");
          text.setAttribute("font-size", this._scaleIconValue ? "44em" : "470");
          text.setAttribute("font-family", '"image2font_' + fontName.trim() + '"');

          if (this._scaleIconValue) {
            this._img.style.setProperty("--scaleIconValue", this._scaleIconValue);
          }
          text.textContent = finalChar;
          if (color) {
            text.setAttribute("fill", color);
          }
          this._img.appendChild(text);
          this._element.appendChild(this._img);
        }
        this.toggleClass("gbc_fixedSvg", !this._autoScale && !this._scaleIconValue);
      },

      /**
       * Appends an <img> tag to the element
       * @param directApply
       * @private
       */
      _appendImage: function (directApply) {
        this._img = document.createElement("img");
        // Decorative by default: empty alt so screen readers skip it instead of reading the file name.
        // setSrc/setTitle override it with the comment text when the image is informative.
        this._img.setAttribute("alt", "");

        this._boundOnError ??= this._onError.bind(this);
        this._boundOnLoad ??= this._onLoad.bind(this);
        cls.DOMElementHelper.on(this._img, "error.ImageWidget", this._boundOnError);
        cls.DOMElementHelper.on(this._img, "load.ImageWidget", this._boundOnLoad);

        if (directApply) {
          this._img.src = this._src;
        } else {
          this._setElementAttribute("src", this._src, "_img");
        }

        this._element.appendChild(this._img);
      },

      /**
       * Set a custom process in the default error handler
       * @param errorHdl
       */
      setErrorHandler: function (errorHdl) {
        this._onErrorHandler = errorHdl;
      },

      /**
       * Error handler in case of wrong loading and other
       * @private
       */
      _onError: function () {
        if (this._element) {
          this.addClasses("hidden");
        }

        if (this._onErrorHandler) {
          this._onErrorHandler();
        }
      },

      /**
       * Load handler to decide what to do after image finished loading
       * @private
       */
      _onLoad: function () {
        if (this._element) {
          this.removeClass("hidden");
          const w = this._img.naturalWidth,
            h = this._img.naturalHeight;
          if (!this.getLayoutEngine().hasNaturalSize()) {
            if (!this._autoScale) {
              this._layoutEngine.invalidateMeasure();
            }
            this.getLayoutEngine()._needMeasure = true;
          }
          this.getLayoutEngine().setNaturalSize(w, h);

          this.getLayoutInformation()._sizeRatio = h / w;

          const isInitial = this.getLayoutInformation().getSizePolicyConfig().isInitial();
          if (isInitial && this._firstInitialSizing) {
            if (this._autoScale) {
              this._initialAutoscaling = true;
              this.getLayoutInformation()._keepRatio = true;
            } else {
              if (!this.getLayoutEngine().hasNaturalSize()) {
                this.getLayoutEngine()._needMeasure = true;
              }
            }
          }

          // with border-radius enabled, enable resize observer to update image ratio axis dominance
          if (this._shouldScale() && this.getImageBorderRadius()) {
            this._observeImageScalingRatio();
          }

          this.emit(context.constants.widgetEvents.ready, this.getLayoutEngine().hasNaturalSize());
          gbc.LogService.ui.log("Image loaded", true, this.__name, this);
        }
      },

      /**
       * Callback once image has been layouted
       * @private
       */
      _whenLayouted: function () {
        if (this._initialAutoscaling) {
          this._initialAutoscaling = false;
          this._firstInitialSizing = false;
          this._updateImage();
        }
      },

      /**
       * @inheritDoc
       */
      setHidden: function (hidden) {
        $super.setHidden.call(this, hidden);
        if (!this._hidden && this._element.parentNode) {
          this._element.parentNode.classList.remove("gl_gridElementHidden");
        }
      },

      getStyleSheetId: function () {
        const windowWidget = this.getWindowWidget(),
          windowWidgetId = windowWidget && windowWidget.getUniqueIdentifier();
        return this._uuid || windowWidgetId || "_";
      },

      /**
       * Get the natural dimension of the image
       * @return {{width: number, height: number}}
       */
      getNaturalDimension: function () {
        return {
          width: this._img.naturalWidth,
          height: this._img.naturalHeight
        };
      },

      /**
       * Return current row index if set (parent widget is a scrollgrid)
       * @returns {?number|number|*}
       */
      getRowIndex: function () {
        if (this.getParentWidget().getRowIndex) {
          return this.getParentWidget().getRowIndex();
        }
        return this._rowIndex;
      },

      /**
       * @inheritDoc
       */
      getContextMenuAuthorizedActions: function () {
        return {
          paste: false,
          copy: true,
          cut: false,
          selectAll: false
        };
      }
    };
  });
  cls.WidgetFactory.registerBuilder("Image", cls.ImageWidget);
});
